import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Modal, ModalHeader, ModalBody, ModalFooter} from 'reactstrap';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {Spinner} from '../../Spinner';
import {SaveButton} from '../../business_units/SaveButton';
import {GoalConfiguration} from '../../business_units/global_info/GoalConfiguration';
import {BusinessUnitGoalApi} from '../../../api/businessUnitGoalApi';
import {typedErrorMessage} from '../../../utils/typedErrorMessage';
import {LineGoalEconomicsSuggestion} from './LineGoalEconomicsSuggestion';
import './LineGoalModal.scss';

// El modal edita los objetivos 1 y 2 (card o1oqjHps: «me permite definir uno solo»). El chip y la
// fila muestran el PRINCIPAL, y la sugerencia de la economía va sólo a él. Más de dos objetivos por
// línea es otra card (LtWUzip0).
const MAIN_PRIORITY = 1;
export const MODAL_PRIORITIES = [1, 2];
const EMPTY_BY_PRIORITY = {1: null, 2: null};
const FALSE_BY_PRIORITY = {1: false, 2: false};

// Marca del `PUT` cuando lo que se guarda es el objetivo sugerido desde la economía del negocio: el
// backend lo registra en la Bitácora como `GOAL_SET_FROM_ECONOMICS` (contrato Economía §3.4).
export const ECONOMICS_SUGGESTION_SOURCE = 'ECONOMICS_SUGGESTION';

/** ¿El objetivo del formulario sigue siendo EXACTAMENTE el sugerido que se aplicó? */
function isSuggestedGoal(goal, applied) {
    return !!goal && !!applied && goal.metricName === applied.metricName
        && Number(goal.value) === Number(applied.value);
}

function goalToFormShape(data) {
    if (!data || !data.has_goal) return null;
    return {
        metricName: data.metric_name,
        value: data.value,
        metricConversionConfiguration: data.metric_conversion_configuration !== undefined
            ? data.metric_conversion_configuration : null,
    };
}

function goalToPutShape(goal) {
    if (!goal) {
        return {has_goal: false, metric_name: null, value: null, metric_conversion_configuration: null};
    }
    return {
        has_goal: true,
        metric_name: goal.metricName,
        value: goal.value,
        metric_conversion_configuration: goal.metricConversionConfiguration !== undefined
            ? goal.metricConversionConfiguration : null,
    };
}

/**
 * Un objetivo prendido necesita métrica y una meta positiva.
 *
 * El endpoint parcial NO lo valida (el paquete `edit` del drawer sí, con `_is_goal_valid`): sin
 * este control el modal guardaba un objetivo sin meta (`value: null`) o una meta 0, y con la
 * métrica vacía o el valor borrado el backend respondía 500. La validación que falta del lado del
 * backend es deuda aparte; ésta es la del lugar donde el usuario puede corregir.
 */
export function goalIsComplete(goal) {
    if (!goal) return true;
    const value = Number(goal.value);
    return !!goal.metricName && goal.value !== '' && goal.value !== null
        && Number.isFinite(value) && value > 0;
}

/**
 * Modal «Definir objetivo» de una línea (card 6zUFUONB, 17/09).
 *
 * El chip vacío abría el drawer del lápiz: presupuesto arriba, período, objetivo y «Desactivar esta
 * línea». Pablo entró a poner un objetivo y «me apareció de todo, te marea eso». Este modal tiene
 * los objetivos y NADA más; quien sí quería el resto tiene el link al pie (`onOpenFull`).
 *
 * Usa el endpoint PARCIAL `business-unit/:id/goals/<1|2>` y no el paquete de `edit`: escribir un
 * objetivo no tiene por qué re-enviar el presupuesto, y así tampoco puede pisarlo. Desde la card
 * o1oqjHps edita los objetivos 1 y 2: un PUT por cada uno que cambió, en orden.
 *
 * ⚠️ Los padres lo montan con `key` por línea (y otra al cerrar): cada apertura nace de cero. Sin
 * eso, el primer render sobre la línea B salía con el estado de A —y `GoalConfiguration` pedía sus
 * datos dos veces—, y un `saving` sobrevivía al cierre.
 *
 * `economy` (bloque del agregado, sólo con el módulo Economía del cliente) suma arriba del
 * formulario el objetivo que sugiere el margen. Lo montan DOS pantallas —el Panel y la vista de
 * todas las líneas—, y las dos le pasan el bloque y el `clientId`.
 */
export function LineGoalModal({line, isOpen, onClose, onSaved, onOpenFull, clientId = null, economy = null}) {
    const {t} = useTranslation();
    const lineId = line ? line.id : null;

    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [goals, setGoals] = useState(EMPTY_BY_PRIORITY);
    // Si la línea YA tenía ese objetivo: apagar el switch sobre uno que no existía no es un cambio, y
    // mandarlo igual dejaba una entrada en la Bitácora y un «Objetivo guardado» que no guardó nada.
    const [hadGoal, setHadGoal] = useState(FALSE_BY_PRIORITY);
    const [changed, setChanged] = useState(FALSE_BY_PRIORITY);
    const [saving, setSaving] = useState(false);
    // «Usar sugerido» tiene que REMONTAR el formulario: `GoalConfiguration` copia `data` a su estado
    // en el constructor y no lo vuelve a leer, así que cambiarle la prop no cambia lo que muestra.
    const [formKey, setFormKey] = useState(0);
    // El objetivo sugerido que se aplicó, para marcar el PUT. Si el usuario lo retoca deja de serlo.
    const [appliedSuggestion, setAppliedSuggestion] = useState(null);
    // La métrica del objetivo principal TAL COMO VINO: la sugerencia (ROAS de la tienda) no se ofrece
    // para reemplazar otra métrica (R-ecofe-01).
    const [loadedMetric, setLoadedMetric] = useState(null);
    // Una respuesta que llega con el modal ya desmontado (cambio de cliente) no toca el estado ni
    // cierra nada: el `onClose` de esta instancia cerraría el modal que esté abierto ahora.
    const mounted = useRef(true);
    // Se re-arma en el efecto (no sólo en el `useRef`): StrictMode monta, desmonta y vuelve a montar.
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    const load = useCallback(() => {
        if (lineId === null) return;
        setLoading(true);
        setFailed(false);
        setChanged(FALSE_BY_PRIORITY);
        const fail = () => {
            if (!mounted.current) return;
            setFailed(true);
            setLoading(false);
        };
        Promise.all(MODAL_PRIORITIES.map((priority) => BusinessUnitGoalApi.get(lineId, priority))).then((responses) => {
            if (!mounted.current) return;
            if (responses.some((response) => !response || response.status !== 'OK' || !response.data)) {
                fail();
                return;
            }
            const nextGoals = {};
            const nextHad = {};
            MODAL_PRIORITIES.forEach((priority, i) => {
                nextGoals[priority] = goalToFormShape(responses[i].data);
                nextHad[priority] = !!responses[i].data.has_goal;
            });
            const main = responses[MODAL_PRIORITIES.indexOf(MAIN_PRIORITY)].data;
            setGoals(nextGoals);
            setHadGoal(nextHad);
            setLoadedMetric(main.has_goal ? (main.metric_name || null) : null);
            setLoading(false);
        }).catch(fail);
    }, [lineId]);

    useEffect(() => {
        if (isOpen) load();
    }, [isOpen, load]);

    const handleGoalChange = (priority) => (data) => {
        setGoals((prev) => ({...prev, [priority]: data}));
        setChanged((prev) => ({...prev, [priority]: true}));
        if (priority === MAIN_PRIORITY && !isSuggestedGoal(data, appliedSuggestion)) setAppliedSuggestion(null);
    };

    const handleUseSuggestion = ({metricName, value}) => {
        const suggested = {metricName, value, metricConversionConfiguration: null};
        setGoals((prev) => ({...prev, [MAIN_PRIORITY]: suggested}));
        setChanged((prev) => ({...prev, [MAIN_PRIORITY]: true}));
        setAppliedSuggestion({metricName, value});
        setFormKey((key) => key + 1);
    };

    /**
     * Un error tipado conocido (métrica que la plataforma no expone) dice qué corregir; el resto va al
     * genérico. Nunca el `error_message` crudo: puede ser el `detail` en inglés de DRF.
     *
     * Con dos objetivos en juego el mensaje dice de CUÁL es, y si el otro ya se guardó lo dice también:
     * «no pudimos guardar los objetivos» con el 1 ya escrito (y el Panel ya refrescado) hacía creer que
     * Cancelar dejaba todo como estaba (review C-…-04).
     */
    const saveError = (response, {priority, savedPriorities, writingMany}) => {
        if (!mounted.current) return;
        setSaving(false);
        const reason = typedErrorMessage(response);
        const vars = {failed: priority, saved: savedPriorities.join(', '), reason,
                      interpolation: {escapeValue: false}};
        if (savedPriorities.length > 0) {
            toast.error(t(reason ? 'client_panel.line_goal_modal_partial_error_reason'
                                 : 'client_panel.line_goal_modal_partial_error', vars));
        } else if (reason && (writingMany || priority !== MAIN_PRIORITY)) {
            toast.error(t('client_panel.line_goal_modal_error_of_goal', vars));
        } else {
            toast.error(reason || t('client_panel.line_goal_modal_save_error'));
        }
    };

    // Lo que hay que escribir: los objetivos que cambiaron, salvo apagar uno que no existía.
    const toWrite = MODAL_PRIORITIES.filter((priority) => changed[priority]
        && !(goals[priority] === null && !hadGoal[priority]));

    // Un PUT por objetivo, en orden. Si el 2 falla con el 1 ya guardado, el 1 queda marcado como
    // guardado (el reintento no lo re-manda) y el padre refresca igual: el dato cambió.
    // Los formularios quedan deshabilitados mientras tanto (`<fieldset disabled>` abajo): cada PUT manda
    // los valores del clic, y lo que se tocara durante el guardado se perdía con toast de éxito (C-…-03).
    const handleSave = (event) => {
        if (event && event.preventDefault) event.preventDefault();
        if (saving || toWrite.length === 0) return;
        setSaving(true);
        const savedPriorities = [];
        const writingMany = toWrite.length > 1;
        const writeNext = (pending) => {
            if (pending.length === 0) {
                // El refetch del padre corre igual aunque el modal ya no esté: el dato cambió. Va con
                // el id: la vista de líneas lo usa para no esconder la línea recién editada.
                if (onSaved) onSaved(lineId);
                if (!mounted.current) return;
                setSaving(false);
                toast.success(t('client_panel.line_goal_modal_saved'));
                onClose();
                return;
            }
            const [priority, ...rest] = pending;
            const goal = goals[priority];
            const body = goalToPutShape(goal);
            if (priority === MAIN_PRIORITY && isSuggestedGoal(goal, appliedSuggestion)) {
                body.source = ECONOMICS_SUGGESTION_SOURCE;
            }
            const failWith = (response) => {
                if (savedPriorities.length > 0 && onSaved) onSaved(lineId);
                saveError(response, {priority, savedPriorities, writingMany});
            };
            BusinessUnitGoalApi.update(lineId, priority, body).then((response) => {
                if (!response || response.status !== 'OK') {
                    failWith(response);
                    return;
                }
                savedPriorities.push(priority);
                if (mounted.current) {
                    setChanged((prev) => ({...prev, [priority]: false}));
                    setHadGoal((prev) => ({...prev, [priority]: goal !== null}));
                }
                writeNext(rest);
            }).catch(() => failWith(null));
        };
        writeNext(toWrite);
    };

    // Mientras viaja el PUT el modal no se cierra: cerrarlo dejaba una respuesta sin dueño que
    // después cerraba (o reportaba un error sobre) el modal de otra línea.
    const requestClose = () => {
        if (!saving) onClose();
    };

    const handleOpenFull = () => {
        onClose();
        onOpenFull(lineId);
    };

    // Completo = lo que se va a ESCRIBIR. Un objetivo que no se tocó no traba el otro aunque esté
    // incompleto en la base (el endpoint parcial, el MCP y el Media Plan lo permiten), y ahí el aviso
    // de «incompleto» no aparecía porque sólo sale si esa prioridad cambió (review C-…-02).
    const complete = toWrite.every((priority) => goalIsComplete(goals[priority]));
    const canSave = toWrite.length > 0 && !loading && !failed && complete;
    const ready = !loading && !failed;

    const renderGoal = (priority) => (
        <div key={priority} className="pnl-goal-modal__goal">
            <GoalConfiguration
                // Sólo el principal se remonta con «Usar sugerido»: el 2 conserva lo que se
                // estaba editando.
                key={`goal-${lineId}-${priority}-${priority === MAIN_PRIORITY ? formKey : 0}`}
                priority={priority}
                businessUnitId={lineId}
                data={goals[priority]}
                // Sólo las plataformas de la línea (card RVO9leLh): una línea de Google ofrecía
                // las 24 métricas de Meta.
                channels={line.channels}
                onChange={handleGoalChange(priority)}
            />
            {priority !== MAIN_PRIORITY && !goals[priority] && (
                <p className="pled-hint pnl-goal-modal__second-hint">
                    {t('client_panel.line_goal_modal_second_hint')}
                </p>
            )}
            {changed[priority] && !goalIsComplete(goals[priority]) && (
                <p className="pled-hint pnl-goal-modal__incomplete">
                    {t('client_panel.line_goal_modal_incomplete')}
                </p>
            )}
        </div>
    );

    const renderBody = () => {
        if (loading) {
            return <div className="box-loader"><Spinner color="positive"/></div>;
        }
        if (failed) {
            return (
                <div className="pled-error">
                    <p>{t('client_panel.line_goal_modal_load_error')}</p>
                    <button type="button" className="btn -negative" onClick={load}>
                        {t('client_panel.error_retry')}
                    </button>
                </div>
            );
        }
        return (
            <fieldset className="pnl-goal-modal__goals" disabled={saving}>
                {MODAL_PRIORITIES.map(renderGoal)}
            </fieldset>
        );
    };

    if (!line) return null;

    const titleId = `pnl-goal-modal-title-${lineId}`;

    return (
        <Modal isOpen={isOpen} toggle={requestClose} className="pnl-goal-modal" labelledBy={titleId}
               backdrop={saving ? 'static' : true} keyboard={!saving}>
            <ModalHeader toggle={requestClose} id={titleId}>
                {t('client_panel.line_goal_modal_title', {name: line.name, interpolation: {escapeValue: false}})}
            </ModalHeader>
            <ModalBody>
                <div className="modal-section">
                    {/* El porqué, con la MISMA frase que el cartel del chip que abrió este modal: eran
                        dos redacciones de la misma idea (QA-VIS-SEGFE-17). */}
                    {ready && !(line.goal && line.goal.defined) && (
                        <p className="pled-hint">{t('client_panel.line_goal_why')}</p>
                    )}
                    {/* Lo que sugiere la economía del negocio (sólo con el módulo). Se monta desde que
                        el modal abre —en la MISMA posición, para que no se remonte— así la sugerencia
                        se pide en paralelo con el objetivo, y se muestra con el formulario listo.
                        Completa el form y lo remonta; guardar sigue siendo el botón de siempre. */}
                    <LineGoalEconomicsSuggestion
                        lineId={lineId}
                        economy={economy}
                        clientId={clientId}
                        onUse={handleUseSuggestion}
                        disabled={saving}
                        hidden={!ready}
                        loadedMetric={loadedMetric}
                        currentGoal={goals[MAIN_PRIORITY]}
                    />
                    {renderBody()}
                </div>
            </ModalBody>
            <ModalFooter>
                {onOpenFull && (
                    <button type="button" className="pnl-goal-modal__full" onClick={handleOpenFull}
                            disabled={saving}>
                        {t('client_panel.line_goal_modal_open_full')}
                    </button>
                )}
                {/* Con borde y no lleno: `.btn` a secas es el ciruela pleno y le ganaba en peso a
                    «Guardar objetivo», que es la acción del modal. */}
                <button type="button" className="btn -negative" onClick={requestClose} disabled={saving}>
                    {t('client_panel.line_goal_modal_cancel')}
                </button>
                <SaveButton
                    text={t('client_panel.line_goal_modal_save')}
                    isSaving={saving}
                    hasChanges={canSave}
                    onClick={handleSave}
                />
            </ModalFooter>
        </Modal>
    );
}

export default LineGoalModal;
